import { createElement } from '../../../core/dom/helpers.js';
import { createOptionsProvider, subscribeWithInitial } from '../../../core/options/options-provider.js';
import { renderOptionContent } from '../../../core/options/render-option-content.js';

/** @type {import('../../../contracts/filters.js').FilterDriver} */
export const radioFilterDriver = {
  type: 'radio',
  mount(container, ctx) {
    const { def, onChange } = ctx;

    const group = createElement('div', {
      class: 'radio-group',
      role: 'group',
      'aria-labelledby': `filter-${def.key}-label`,
    });
    container.appendChild(group);

    if (def.loadOptions) {
      // --- Async path ---
      let destroyed = false;
      let currentValue = ctx.value != null ? String(ctx.value) : '';

      /** @type {ReadonlyArray<import('../../../contracts/forms.js').FieldOption> | null} */
      let prevOptions = null;

      /** @type {HTMLInputElement[]} */
      let radios = [];

      const provider = createOptionsProvider({
        options: def.options,
        loadOptions: def.loadOptions,
        mapOption: def.mapOption,
      });

      /**
       * Creates radio DOM for given options. Does NOT prune currentValue.
       * @param {import('../../../contracts/forms.js').FieldOption[]} options
       */
      function renderRadios(options) {
        group.textContent = '';
        radios = [];
        for (const opt of options) {
          const radioId = `filter-${def.key}-${opt.value}`;
          const radioLabel = createElement('label', { class: 'radio-label', for: radioId });
          const radio = /** @type {HTMLInputElement} */ (createElement('input', {
            type: 'radio',
            id: radioId,
            name: `filter-${def.key}`,
            value: opt.value,
          }));
          if (currentValue === opt.value) {
            radio.checked = true;
          }
          radio.addEventListener('change', () => {
            if (radio.checked) {
              currentValue = radio.value;
              onChange(currentValue);
            }
          });
          radios.push(radio);
          radioLabel.appendChild(radio);
          const labelContent = createElement('span', {});
          renderOptionContent(opt, labelContent);
          radioLabel.appendChild(labelContent);
          group.appendChild(radioLabel);
        }
      }

      /**
       * Rebuilds radios and prunes currentValue if not in available options.
       * Fires onChange if value was lost.
       * @param {import('../../../contracts/forms.js').FieldOption[]} options
       */
      function rebuildRadios(options) {
        const prevValue = currentValue;
        renderRadios(options);

        // Prune: if currentValue not in new options, clear it
        const valueExists = options.some(o => o.value === currentValue);
        if (!valueExists && currentValue !== '') {
          currentValue = '';
        }

        if (currentValue !== prevValue) {
          onChange(currentValue);
        }
      }

      const unsubscribe = subscribeWithInitial(provider, (state) => {
        if (destroyed) return;

        // Always update loading UI
        group.setAttribute('aria-busy', state.loading ? 'true' : 'false');

        // Only rebuild when options actually changed
        if (state.options !== prevOptions) {
          if (prevOptions === null) {
            // Initial render: create radios without pruning
            prevOptions = state.options;
            renderRadios(state.options);
          } else {
            prevOptions = state.options;
            rebuildRadios(state.options);
          }
        }
      });

      provider.load('');

      return {
        setValue(value) {
          currentValue = value != null ? String(value) : '';
          for (const radio of radios) {
            radio.checked = radio.value === currentValue;
          }
        },
        getValue() {
          return currentValue;
        },
        destroy() {
          destroyed = true;
          unsubscribe();
          provider.destroy();
          group.remove();
        },
      };
    }

    // --- Static path (backward compatible) ---

    /** @type {HTMLInputElement[]} */
    const radios = [];

    for (const opt of def.options ?? []) {
      const radioId = `filter-${def.key}-${opt.value}`;
      const radioLabel = createElement('label', { class: 'radio-label', for: radioId });
      const radio = /** @type {HTMLInputElement} */ (createElement('input', {
        type: 'radio',
        id: radioId,
        name: `filter-${def.key}`,
        value: opt.value,
      }));
      if (ctx.value === opt.value) {
        radio.checked = true;
      }
      radio.addEventListener('change', () => {
        if (radio.checked) onChange(radio.value);
      });
      radios.push(radio);
      radioLabel.appendChild(radio);
      const labelContent = createElement('span', {});
      renderOptionContent(opt, labelContent);
      radioLabel.appendChild(labelContent);
      group.appendChild(radioLabel);
    }

    return {
      setValue(value) {
        for (const radio of radios) {
          radio.checked = radio.value === value;
        }
      },
      getValue() {
        const checked = radios.find(r => r.checked);
        return checked ? checked.value : '';
      },
      destroy() {
        group.remove();
      },
    };
  },
};
